"use client";

import { useState, useEffect, useCallback } from "react";
import { TaskTemplate } from "@/lib/types/task-templates";

const STORAGE_KEY = "wtc_custom_task_templates";

export const useCustomTemplates = () => {
  const [customTemplates, setCustomTemplates] = useState<TaskTemplate[]>([]);

  // Load templates from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setCustomTemplates(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.error("Error loading custom templates:", error);
      setCustomTemplates([]);
    }
  }, []);

  // Save templates to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(customTemplates));
    } catch (error) {
      console.error("Error saving custom templates:", error);
    }
  }, [customTemplates]);

  const saveTemplate = useCallback((template: Omit<TaskTemplate, "id">) => {
    const newTemplate: TaskTemplate = {
      ...template,
      id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };

    setCustomTemplates((prev) => [...prev, newTemplate]);
    return newTemplate;
  }, []);

  const updateTemplate = useCallback((id: string, updates: Partial<TaskTemplate>) => {
    setCustomTemplates((prev) =>
      prev.map((template) => (template.id === id ? { ...template, ...updates } : template))
    );
  }, []);

  const deleteTemplate = useCallback((id: string) => {
    setCustomTemplates((prev) => prev.filter((template) => template.id !== id));
  }, []);

  const getTemplate = useCallback(
    (id: string) => {
      return customTemplates.find((template) => template.id === id);
    },
    [customTemplates]
  );

  return {
    customTemplates,
    saveTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplate,
  };
};
